// Seed content for the builder: the blank form, the canned example, and the
// prompt users paste into an external LLM when importing from a paper.

export const DEFAULT_BALANCE = `# Natural-gas power plant. Anchor is the gas inlet (mass basis).
# Outputs: electricity, CO2 (combustion), CH4 (uncombusted leak).

# A small fraction of the gas escapes uncombusted as methane.
leak = gas * leak_fraction
ch4_leak = leak

# Electricity = heat content of the combusted fraction × thermal efficiency.
# gas.energy_lhv resolves to mmBtu (natural gas's idiomatic basis); Sesame
# converts to kWh at the electricity output boundary.
electricity = gas.energy_lhv * (1 - leak_fraction) * efficiency

# close_carbon_balance() returns the slack carbon (entering minus
# outputs declared above) converted to CO2 mass — keep this last among
# the carbon-bearing outputs so the residual is correct.
co2 = close_carbon_balance()
`;

// Meta-prompt for the "Build from a document" flow. User pastes this
// alongside a PDF / paper into Claude.ai or ChatGPT, then pastes the
// structured response back into the AI assistant — our agent reads it like
// any other message and proposes module_update + run_test.
export const EXTERNAL_IMPORT_PROMPT = `You're helping me build a process model in Sesame (an LCA / TEA modeling platform). Read the attached document and produce a model that's **as detailed as the platform can faithfully express** — not a verbatim transcription of the paper, but not a black-box one-liner either.

What Sesame DOES model:
- Mass-balanced flows of inputs and outputs (kg, kWh, mmBtu, etc.).
- Live species properties that propagate through the pathway — heating values (lhv, hhv), carbon_ratio, density, molar_mass. Reference them in your equations so the model responds when the user customizes them upstream (e.g. swaps natural gas for biogas).
- Algebraic expressions tying inputs/outputs/parameters together.

What Sesame does NOT model:
- Live thermodynamics or kinetics. Turbine inlet temperature × isentropic efficiency doesn't produce an exit state; SOFC voltage × current density doesn't produce a power output. If the paper derives a value through such a simulation, encode the **result** as a parameter (e.g. \`net_efficiency: 0.692\`), not the simulation inputs.
- Component-level state, residence times, transients, or part-load behavior.

Produce three sections:

INPUTS / OUTPUTS
List each material or energy flow that appears in the balance. For each: a label, a species name (e.g. natural_gas, electricity, co2, hydrogen, water, methane, oxygen, steam, ammonia), and whether it's an INPUT or OUTPUT. The first input is the **anchor** — the per-unit flow your balance scales against. Mark emissions outputs explicitly. If two outputs share a species (e.g. captured CO2 product + emitted CO2 vent), note which is the emissions one.

For each non-emission output, also note whether it's an **allocatable product** (a real product worth attributing environmental burden to) or a **non-allocatable byproduct** (managed waste, condensate, low-grade heat). LCA allocation needs a common dimension: a power plant with electricity + captured CO2 as outputs needs electricity allocatable and captured CO2 non-allocatable. A co-production process where both products are revenue streams (H2 + steam) can have both allocatable.

PARAMETERS
A parameter earns its place if (a) it appears in the balance AND (b) a user might reasonably want to vary it for scenario analysis (efficiency, capture rate, leak fraction). DO NOT include sub-component values that only matter via their effect on a headline number you're already using — if the paper reports both "net electric efficiency 69.2%" and the breakdown into SOFC voltage / turbine isentropic eff / gearbox eff / alternator eff, use only the net efficiency. The sub-components belong in the description as context.

For each parameter: a label, a unit, and a numeric default. Valid units: scalar, %, kg/kg, g/kg, degC, K, bar, kg, kWh, MWh, MJ, mmBtu, kWh/kg, MJ/kg, mmBtu/kg, mmBtu/ton, kW, MW, hr, day, year. IMPORTANT: \`%\` means a decimal fraction in [0, 1] — 60% efficiency is \`0.6\`, not \`60\`. If the paper doesn't give a concrete number, omit the parameter.

For compound-unit parameters where the mass/energy basis is species-specific, put the species clarifier in parentheses at the END of the label: "Electricity load (per kg NG)", "Steam consumption (per kg H2)". Parenthetical content is stripped from the slug, so it only affects the human-visible label, not balance expressions.

BALANCE EQUATIONS
Write the balance as a list of \`name = expression\` assignments, one per line. Use only \`+ - * / ** sqrt log exp min max abs\`. Tie each output to the physics that drives it (energy balance for power, stoichiometry + carbon_ratio for combustion CO2, mass ratios for byproducts).

Key rules:

- **Outputs are readable after they're assigned.** \`methanol = co2 * 0.85 * (32/44); water = methanol * (18/32); hydrogen = methanol * (6.048/32)\` works — \`water\` and \`hydrogen\` can reference the just-assigned \`methanol\`. Each output can be written ONCE; reassignment errors. Read-before-write also errors.
- **Intermediate variables (let-bindings) are fine for clarity.** Use \`fuel_mass = ...\` if it makes the balance readable. But don't add them just to document — use \`# comments\` for that.
- **\`set <output>.<property> = expr\`** at the top of the balance overrides an output property — useful when the process changes a downstream value (compressing CO2 to 110 bar, raising a product temperature). Outputs only; input properties propagate from upstream and shouldn't be overridden. Use sparingly — only when the process physically changes the property AND it differs from the species default. Skip for ambient / near-default values.
- **\`close_carbon_balance()\`** is the canonical pattern for residual carbon outputs (CO2 vent, CH4 leak). As the last carbon-bearing output line, it computes the slack between carbon in (anchor + inputs) and carbon already declared in outputs above, then assigns the residual to the LHS species's mass basis.

Reference species properties as \`<io_slug>.<property>\`: e.g. \`natural_gas.carbon_ratio\`, \`hydrogen.molar_mass\`. Read live properties; only hardcode true universal constants (atomic masses ~12.011 for C, ~16.0 for O, ~1.008 for H; molar masses of pure species like CH4 ≈ 16.04, H2O ≈ 18.015, CO2 ≈ 44.01; stoichiometric coefficients).

**Conversion accessors** convert an IO's flow to a different basis. Use these instead of doing the math by hand:

- \`<io>.energy_lhv\` → flow as energy (mmBtu for NG, kWh for J-declared species)
- \`<io>.energy_hhv\` → same but HHV basis
- \`<io>.mass\` → kg
- \`<io>.volume\` → m³
- \`<io>.moles\` → mol

Available on the anchor (always — its flow is known) and on any non-anchor input or output that's been assigned earlier in the balance. Read-before-write errors.

For NG and other Btu-declared species, \`.lhv\` and \`.hhv\` are **volumetric** (Btu/ft³), not mass-basis — don't multiply \`io.mass * io.lhv\` to derive energy, and don't divide \`io.energy_lhv / io.lhv\` to derive mass. Just use \`.energy_lhv\` / \`.mass\` directly.

**Output boundary auto-conversion**: when you assign an energy / volume / moles value to a mass output, the framework converts at the IO boundary using the output species's properties. So \`hydrogen = natural_gas.energy_lhv * cold_gas_efficiency\` just works — \`hydrogen\` is computed in mmBtu and converted to kg at the boundary via hydrogen's LHV. Don't create manual \`h2_lhv\` / \`ng_lhv\` parameters when the species registry already has the data.

**Energy units mix freely** in expressions — Sesame auto-converts same-dimension quantities for add, subtract, and divide. You don't need to manually normalize "thermal_input is mmBtu but asu_power is kWh." Write naturally.

Sesame validates the balance with a **carbon balance check** on every test — it sums carbon entering (anchor + inputs × carbon_ratio) and leaving (outputs × carbon_ratio), and reports any slack. Aim for a closing balance. If your process has a real residual emissions stream (a slip, a vent, unreacted CO2), add it as a carbon-bearing output (or use \`close_carbon_balance()\`).

ECONOMICS (optional)
If the document quantifies costs — capital cost, fixed and variable operating costs, capacity factor, reference capacity, per-product unit costs — list them with units and a citation (table or section). Skip this section entirely if the document doesn't discuss economics.

GENERAL RULES
- Use lowercase snake_case identifiers (\`natural_gas\`, \`cold_gas_efficiency\`, not \`m_dot_NG\` or \`η_thermal\`).
- If something isn't in the document, say so in a "Not in the document" note — don't invent values.`;

// Both of these are factories rather than shared constants. `reset()` seeds
// react-hook-form's field arrays from whatever object it's handed, and rows
// the user then edits can write back through the same references — so a
// module-level literal would carry edits from one "Load example" into the
// next. Building fresh each time is the only way a reset is really a reset.

// The form starts here: one blank input, one blank output, no params, TEA off.
export const blankDefaults = (defaultSpecies = '') => ({
  label: '',
  description: '',
  permissions: 'creator',
  project_ids: [],
  inputs: [{ label: '', species: defaultSpecies }],
  outputs: [{ label: '', species: defaultSpecies, emissions: false, allocatable: true }],
  params: [],
  balance: '',
  tea: {
    mode: 'none',
    reference_product: '',
    reference_capacity: 0,
    reference_capacity_unit: 'MW',
    capacity_factor: 0.85,
    capital_cost: 0,
    fixed_operating_costs: 0,
    fixed_maintenance_costs: 0,
    variable_operating_costs: 0,
    scaling_exponent: 0,
    simple_costs: {},
  },
});

// Example template — populated by the "Load example" button. Kept
// separate from the blank-form defaults so the modal starts empty.
export const exampleDefaults = () => ({
  label: 'Test Equation Module',
  description: 'Built via the equation builder.',
  permissions: 'creator',
  inputs: [
    { label: 'Gas', species: 'natural_gas' },
  ],
  outputs: [
    { label: 'Electricity', species: 'electricity', emissions: false, allocatable: true },
    { label: 'CO2', species: 'co2', emissions: true, allocatable: false },
    { label: 'CH4 Leak', species: 'ch4', emissions: true, allocatable: false },
  ],
  params: [
    { label: 'Efficiency', type: 'numeric', unit: '%', default: 0.55 },
    { label: 'Leak Fraction', type: 'numeric', unit: '%', default: 0.005 },
  ],
  balance: DEFAULT_BALANCE,
  tea: {
    mode: 'none',
    reference_product: '',
    reference_capacity: 500,
    reference_capacity_unit: 'MW',
    capacity_factor: 0.85,
    capital_cost: 1_500_000_000,
    fixed_operating_costs: 50_000_000,
    fixed_maintenance_costs: 20_000_000,
    variable_operating_costs: 0.005,
    scaling_exponent: 0,
    simple_costs: {},
  },
});
